import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Segment } from "@spoonity/flags-contract";
import { createSegment, listSegments } from "../../api/endpoints";
import { canWrite, useAuth } from "../../auth/AuthContext";
import { EmptyState } from "../../components/EmptyState";
import { LoadMore } from "../../components/LoadMore";
import { useToast } from "../../components/Toast";
import { useWorkspaceEnv } from "../../state/WorkspaceEnvContext";

export function SegmentsListPage() {
  const { environment } = useWorkspaceEnv();
  const { identity } = useAuth();
  const { pushError, push } = useToast();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!environment) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const page = await listSegments(environment.id, { limit: 50 });
        if (cancelled) return;
        setSegments(page.items);
        setNextCursor(page.nextCursor);
      } catch (err) {
        if (!cancelled) pushError(err, "Could not load segments");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [environment, pushError]);

  async function loadMore() {
    if (!environment || !nextCursor) return;
    setLoading(true);
    try {
      const page = await listSegments(environment.id, { cursor: nextCursor, limit: 50 });
      setSegments((cur) => [...cur, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      pushError(err, "Could not load segments");
    } finally {
      setLoading(false);
    }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!environment) return;
    setCreating(true);
    try {
      const segment = await createSegment(environment.id, {
        name: newName,
        description: newDescription || undefined,
      });
      setSegments((cur) => [segment, ...cur]);
      setNewName("");
      setNewDescription("");
      push({ kind: "success", title: `Segment "${segment.name}" created` });
    } catch (err) {
      pushError(err, "Could not create segment");
    } finally {
      setCreating(false);
    }
  }

  if (!environment) return null;

  return (
    <div>
      {canWrite(identity?.role) && (
        <form className="card" onSubmit={create}>
          <h3>New segment</h3>
          <div className="row wrap">
            <input
              className="select"
              placeholder="segment-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              aria-label="Segment name"
              required
            />
            <input
              className="select"
              placeholder="description (optional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              aria-label="Segment description"
            />
            <button className="btn primary" type="submit" disabled={creating || !newName}>
              Create
            </button>
          </div>
        </form>
      )}

      {segments.length === 0 && !loading ? (
        <EmptyState title="No segments yet" />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Keys</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((s) => (
              <tr key={s.name}>
                <td>
                  <Link to={`/segments/${s.name}`}>{s.name}</Link>
                </td>
                <td>{s.description || <span className="muted">—</span>}</td>
                <td>{s.keyCount}</td>
                <td>{new Date(s.updatedAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <LoadMore nextCursor={nextCursor} loading={loading} onLoadMore={loadMore} />
    </div>
  );
}
